import type { GeneralPoolInfo, TierInfo } from '@/types/api'
import { Card } from '@/components/shared/Card'

interface Props {
  pools: GeneralPoolInfo[]
  tiers: TierInfo[]
}

function pctColor(pct: number): string {
  if (pct >= 95) return 'bg-brand-red'
  if (pct >= 75) return 'bg-brand-yellow'
  return 'bg-brand-green'
}

export function GeneralDashboard({ pools, tiers }: Props) {
  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {pools.map(pool => (
          <Card key={pool.geo_code}>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand-blue/8 text-brand-blue text-sm font-bold">
                {pool.geo_code}
              </span>
              <span className="font-semibold text-sm text-text-primary">{pool.geo_name || pool.geo_code}</span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-text-muted mb-1.5">
              <span>Pool Usage</span>
              <span>{pool.pool_pct}%</span>
            </div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${pctColor(pool.pool_pct)}`}
                style={{ width: `${pool.pool_pct}%` }}
              />
            </div>
          </Card>
        ))}
      </div>

      {/* Tier Reference Table */}
      {tiers.length > 0 && (
        <Card>
          <h3 className="font-semibold text-sm text-text-primary mb-3">Tier Reference</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-text-muted font-medium border-b border-gray-100">Tier</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-text-muted font-medium border-b border-gray-100">Payout</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-text-muted font-medium border-b border-gray-100">Margin</th>
                </tr>
              </thead>
              <tbody>
                {tiers.filter(t => t.is_active).map(tier => (
                  <tr key={tier.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 border-b border-gray-100 font-medium text-text-primary">{tier.display_name}</td>
                    <td className="px-3 py-2 border-b border-gray-100 text-text-secondary font-mono">${tier.pub_payout.toFixed(2)}</td>
                    <td className="px-3 py-2 border-b border-gray-100 text-text-muted font-mono">{(tier.margin_pct * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
